import React, {
  forwardRef,
  memo,
  useCallback,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
} from 'react';
import { Col, Input, Row, Tooltip, Avatar } from 'antd';
import { UserOutlined } from '@ant-design/icons';
import { GiSoundWaves } from 'react-icons/gi';
import { MdCancel } from 'react-icons/md';
import { FaClock, FaMicrophone, FaRecordVinyl } from 'react-icons/fa';
import { IoAttach, IoSend } from 'react-icons/io5';
import { useDispatch, useSelector } from 'react-redux';

import ComposerAttachments from './ComposerAttachments';
import ComposerEmojiPicker from './ComposerEmojiPicker';
import Loader from '../Loader';
import Text from '../Text';
import { Colors, Utils } from '../../config';
import { TYPING_IDLE_MS } from '../../config/constants';
import { isRealtimeUsable, SOCKET_DOWN_MESSAGE } from '../../config/socket/socket';
import { MsgAction } from '../../store/actions';

const MAX_ATTACHMENTS = 5;
const MAX_VIDEO_SECONDS = 60;

/**
 * The 1:1 composer: draft text, attachments, voice note, send.
 *
 * Same rationale as GroupComposer — every piece of state here used to live on
 * `MessagesChat`, the component that renders the message stream, so a keystroke
 * re-rendered every loaded bubble and the recording timer did it once a second for a
 * minute. Moving the state below the stream means a keystroke re-renders this subtree
 * and nothing else.
 *
 * No mention picker: a direct chat has no roster to mention.
 *
 * Keeps the optimistic-send behaviour the pane had — a text bubble appears at reduced
 * opacity immediately and settles on the ack, matched by `temp_id`.
 *
 * @param channelId    target channel
 * @param userData     the signed-in user (sender_id)
 * @param socket       shared socket; a parked one must not be emitted into
 * @param replyOnMsg   message being replied to; owned by the parent (the stream sets it)
 * @param onClearReply clears the parent's reply state
 * @param emitTyping   `(boolean) => void` — this pane's signature takes no channel
 * @param onSent       called after a successful send, for the parent's unread divider
 *                     and scroll-to-latest trigger
 * @param onOpenSchedule opens the parent's schedule-message modal
 *
 * Exposes `{ acceptFiles }` on its ref. The drag-and-drop target is the message stream,
 * which belongs to the parent, but the attachment tray belongs here — so the parent
 * forwards dropped files in rather than owning the attachment state itself.
 */
const DirectComposer = forwardRef(function DirectComposer(
  { channelId, userData, socket, replyOnMsg, onClearReply, emitTyping, onSent, onOpenSchedule },
  ref
) {
  const dispatch = useDispatch();

  const uploadMediaLoader = useSelector((state) => state?.MsgReducer?.upload_chat_loader);
  const chatMedia = useSelector((state) => state?.MsgReducer?.upload_chat_media?.data?.chatMedia);

  const [messageInput, setMessageInput] = useState('');
  const [selectedImages, setSelectedImages] = useState([]);
  const [audioUrl, setAudioUrl] = useState(null);
  const [isRecording, setIsRecording] = useState(false);
  const [mediaRecorder, setMediaRecorder] = useState(null);
  const [elapsedTime, setElapsedTime] = useState(0);

  const inputRef = useRef(null);
  const audioChunks = useRef([]);
  const intervalRef = useRef(null);
  const typingStopTimer = useRef(null);
  /**
   * The live microphone capture, so it can be released on the way out.
   *
   * `recorder.onstop` releases it on the normal path. This ref covers the path it
   * cannot: unmounting while still recording never calls `stop()`, so `onstop` never
   * fires and the capture would stay open — with the browser's recording indicator
   * lit — for the life of the tab.
   */
  const streamRef = useRef(null);

  // Changing conversation must not carry a half-typed draft across, or leave a
  // recording timer running.
  useEffect(() => {
    setMessageInput('');
    setSelectedImages([]);
    setAudioUrl(null);
  }, [channelId]);

  useEffect(
    () => () => {
      clearInterval(intervalRef.current);
      clearTimeout(typingStopTimer.current);
      // Last resort for the microphone — see the note on `streamRef`.
      streamRef.current?.getTracks().forEach((track) => track.stop());
      streamRef.current = null;
    },
    []
  );

  useEffect(() => {
    if (selectedImages.length === 0) return;
    const formData = new FormData();
    selectedImages.forEach((file) => formData.append('media', file));
    dispatch(MsgAction.UploadChatMedia(formData));
  }, [selectedImages, dispatch]);

  const uploadChatMedia = useCallback(
    (formData) =>
      new Promise((resolve, reject) => {
        dispatch(
          MsgAction.UploadChatMedia(formData, (data) => {
            const res = data?.data?.chatMedia;
            if (res) resolve(res);
            else reject(new Error('Failed to get chatMedia'));
          })
        );
      }),
    [dispatch]
  );

  const handleChange = (event) => {
    setMessageInput(event.target.value);
    emitTyping?.(true);
    clearTimeout(typingStopTimer.current);
    typingStopTimer.current = setTimeout(() => emitTyping?.(false), TYPING_IDLE_MS);
  };

  // ── voice note ─────────────────────────────────────────────────────────────

  const formatTime = (seconds) =>
    `${String(Math.floor(seconds / 60)).padStart(2, '0')}:${String(seconds % 60).padStart(2, '0')}`;

  const toggleRecording = async () => {
    try {
      if (isRecording) {
        mediaRecorder?.stop();
        setIsRecording(false);
        clearInterval(intervalRef.current);
        return;
      }

      setAudioUrl(null);
      audioChunks.current = [];

      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      streamRef.current = stream;
      const recorder = new MediaRecorder(stream);

      recorder.ondataavailable = (event) => audioChunks.current.push(event.data);

      recorder.onstop = async () => {
        // MediaRecorder emits webm/ogg/mp4 (never mp3) — the real mime type and a
        // matching extension, so playback works and S3 serves the right content-type.
        const mimeType = recorder.mimeType || 'audio/webm';
        const ext = mimeType.includes('ogg') ? 'ogg' : mimeType.includes('mp4') ? 'm4a' : 'webm';
        const audioBlob = new Blob(audioChunks.current, { type: mimeType });
        setAudioUrl(audioBlob);
        try {
          const formData = new FormData();
          formData.append('media', audioBlob, `recording.${ext}`);
          await uploadChatMedia(formData);
        } catch {
          setAudioUrl(null);
          Utils.showMessage('error', 'Failed to upload the audio message. Please try again.');
        }
        audioChunks.current = [];
        // Release the microphone, so the browser stops showing the recording
        // indicator once the note has been sent.
        stream.getTracks().forEach((track) => track.stop());
        streamRef.current = null;
      };

      recorder.start();
      setMediaRecorder(recorder);
      setIsRecording(true);
      setElapsedTime(0);
      intervalRef.current = setInterval(() => {
        setElapsedTime((previous) => {
          const next = previous + 1;
          if (next >= 60) {
            recorder.stop();
            setIsRecording(false);
            Utils.showMessage('error', 'Voice messages are limited to 1 minute');
            clearInterval(intervalRef.current);
          }
          return next;
        });
      }, 1000);
    } catch {
      // If getUserMedia resolved and MediaRecorder construction is what threw, the
      // capture is already open — release it rather than leaving it to the unmount.
      streamRef.current?.getTracks().forEach((track) => track.stop());
      streamRef.current = null;
      setIsRecording(false);
      Utils.showMessage(
        'error',
        'Could not access the microphone. Check your browser permissions.'
      );
    }
  };

  // ── attachments ────────────────────────────────────────────────────────────

  const addFiles = useCallback((files) => {
    files?.forEach((file) => {
      if (file.type.startsWith('image/')) {
        setSelectedImages((previous) => [...previous, file]);
      }
      if (file.type.startsWith('video/')) {
        const videoElement = document.createElement('video');
        videoElement.src = URL.createObjectURL(file);
        videoElement.onloadedmetadata = () => {
          if (videoElement.duration > MAX_VIDEO_SECONDS) {
            Utils.showMessage(
              'warning',
              `Videos must be under ${MAX_VIDEO_SECONDS} seconds. That clip was not attached.`,
              { key: 'attachment-video-length' }
            );
          } else {
            setSelectedImages((previous) => [...previous, file]);
          }
          // Was never released, so every rejected clip leaked its decoded blob for
          // the lifetime of the tab.
          URL.revokeObjectURL(videoElement.src);
        };
      }
    });
  }, []);

  const handleFileInputChange = (event) => {
    const files = Array.from(event.target.files);
    if (files?.length > MAX_ATTACHMENTS) {
      // Keyed, so selecting a batch cannot stack one toast per file.
      Utils.showMessage('warning', `You can attach up to ${MAX_ATTACHMENTS} files at a time.`, {
        key: 'attachment-limit',
      });
      return;
    }
    if (selectedImages?.length <= MAX_ATTACHMENTS) addFiles(files);
    event.target.value = null;
  };

  /** Files dropped on the stream, forwarded in by the parent through the ref. */
  useImperativeHandle(
    ref,
    () => ({
      acceptFiles: (fileList) => {
        const files = Array.from(fileList ?? []);
        if (files.length > MAX_ATTACHMENTS) {
          Utils.showMessage('warning', `You can attach up to ${MAX_ATTACHMENTS} files at a time.`, {
            key: 'attachment-limit',
          });
          return;
        }
        addFiles(files);
      },
    }),
    [addFiles]
  );

  // ── send ───────────────────────────────────────────────────────────────────

  const canSend = Boolean(messageInput.trim() || audioUrl || selectedImages.length);

  const sendMessage = () => {
    if (!(selectedImages.length || messageInput?.length || audioUrl)) return;

    // A parked connection (`active` false and not connected) never flushes its buffer,
    // and Socket.IO reports nothing when an emit goes into it — so the send has to be
    // refused here rather than silently swallowed. A socket that is merely reconnecting
    // still has `active` true; buffering for that one is correct and the ack lands when
    // it comes back.
    if (!isRealtimeUsable(socket)) {
      Utils.showMessage('error', SOCKET_DOWN_MESSAGE, { key: 'socket-offline' });
      return;
    }

    // Held so a rejected send can hand the text back rather than eating it.
    const draft = messageInput;

    /**
     * Optimistic bubble for a plain text message.
     *
     * Text only, deliberately: a media send has already uploaded by this point and
     * carries its own progress in the attachment tray, and an optimistic media bubble
     * would need a local object URL swapped for the S3 one — a second reconciliation
     * path for no gain in perceived speed.
     */
    const optimistic = !audioUrl && selectedImages.length === 0 && draft.trim();
    const tempId = optimistic ? `local-${userData?.id}-${performance.now()}` : null;

    if (tempId) {
      dispatch(
        MsgAction.SendMsg([
          {
            temp_id: tempId,
            // No `id`: an optimistic row has no server identity yet, and giving it one
            // would let a menu action address a message that cannot be found.
            channel_id: channelId,
            sender_id: userData?.id,
            message: draft,
            type: 'TEXT',
            media: null,
            createdAt: new Date().toISOString(),
            pending: true,
          },
        ])
      );
    }

    try {
      clearTimeout(typingStopTimer.current);
      emitTyping?.(false);

      socket.emit(
        'message:send',
        {
          sender_id: userData?.id,
          channel_id: channelId,
          reply_id: replyOnMsg?.id ?? replyOnMsg?.message_id ?? replyOnMsg?.message_ID ?? null,
          message: selectedImages.length > 0 ? null : messageInput,
          type: audioUrl ? 'AUDIO' : selectedImages.length > 0 ? 'MEDIA' : 'TEXT',
          media: chatMedia ?? null,
          current_location: null,
          duration: audioUrl ? elapsedTime : null,
        },
        (item) => {
          const failure = Utils.ackError(item);
          if (failure) {
            Utils.showMessage('error', failure);
            // The optimistic row stays on screen, marked failed. Removing it would
            // leave the sender looking at a conversation where the message they wrote
            // simply is not — which reads as sent.
            if (tempId) dispatch(MsgAction.MessageSettled({ temp_id: tempId, settled: false }));
            setMessageInput((current) => current || draft);
            return;
          }

          const settled = {
            channel_id: item?.response?.channel_id,
            id: item?.response?.id,
            media: item?.response?.media,
            reply_id: item?.response?.reply_id,
            replied_message: item?.response?.replied_message,
            message: item?.response?.message,
            type: item?.response?.type,
            createdAt: item?.response?.createdAt,
            statusMsg: item?.response?.status,
            sender_id: item?.response?.sender_id,
            chat_reactions: item?.response?.chat_reactions,
          };

          // Replace the optimistic row in place when there is one — the bubble keeps
          // its position and its key, so it settles rather than being swapped for an
          // identical-looking new one.
          dispatch(
            tempId
              ? MsgAction.MessageSettled({ temp_id: tempId, message: settled })
              : MsgAction.SendMsg([settled])
          );
          onSent?.();
        }
      );

      setMessageInput('');
      setSelectedImages([]);
      setAudioUrl(null);
      onClearReply?.();
    } catch {
      // Was an empty block, so a throw here — a null socket, most often — left the
      // composer looking as though the message had gone.
      Utils.showMessage('error', 'Message could not be sent');
      if (tempId) dispatch(MsgAction.MessageSettled({ temp_id: tempId, settled: false }));
      setMessageInput((current) => current || draft);
    }
  };

  const attachmentsBusy = selectedImages.length > 0;
  const textBusy = messageInput?.length > 0;

  const suffix = (
    <span style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
      <span
        title={isRecording ? 'Stop recording' : 'Voice note'}
        className={`composer-icon-btn ${attachmentsBusy || textBusy ? 'cursor-not-allowed' : ''}`}
        style={
          isRecording ? { color: 'var(--accent)', animation: 'recPulse 1s infinite' } : undefined
        }
        onClick={attachmentsBusy || textBusy ? undefined : toggleRecording}
      >
        {isRecording ? <FaRecordVinyl size={18} /> : <FaMicrophone size={18} />}
      </span>
      <span
        title="Attach"
        className={`composer-icon-btn ${isRecording || audioUrl || textBusy ? 'cursor-not-allowed' : ''}`}
        style={{
          pointerEvents: selectedImages.length >= MAX_ATTACHMENTS ? 'none' : undefined,
          opacity: selectedImages.length >= MAX_ATTACHMENTS ? 0.5 : undefined,
        }}
        onClick={
          isRecording || audioUrl || textBusy
            ? undefined
            : () => document.getElementById('fileInput')?.click()
        }
      >
        <IoAttach size={20} />
      </span>
    </span>
  );

  const sendButton = canSend ? (
    <button
      type="button"
      title={uploadMediaLoader ? 'Sending…' : 'Send'}
      className={`composer-send-btn ${uploadMediaLoader || isRecording ? 'cursor-not-allowed' : ''}`}
      disabled={uploadMediaLoader || isRecording}
      onClick={uploadMediaLoader ? undefined : sendMessage}
    >
      {/* The glyph becomes a spinner while an upload is in flight. The button
          previously sat unchanged and merely stopped responding, which is
          indistinguishable from a dead button. */}
      {uploadMediaLoader ? (
        <Loader.Circular size={'small'} color="currentColor" />
      ) : (
        <IoSend size={18} />
      )}
    </button>
  ) : (
    <Tooltip placement="top" title={'Schedule Message'}>
      <button type="button" className="composer-send-btn" onClick={onOpenSchedule}>
        <FaClock size={17} />
      </button>
    </Tooltip>
  );

  return (
    <>
      {replyOnMsg && (
        <Row
          style={{ backgroundColor: Colors.backGroundColor }}
          className={`${selectedImages?.length > 0 ? 'mb-16' : ''}`}
        >
          <Col span={23} className="mt-3 h-16">
            <div className="composer-reply p-2">
              <Col span={24}>
                <Text.Basic
                  style={{ fontWeight: '600', fontSize: '0.8rem' }}
                  className="text-[#2B3674] dark:text-white"
                  text={replyOnMsg?.user?.first_name}
                />
              </Col>
              {replyOnMsg?.type == 'TEXT' ? (
                <Text.Basic
                  style={{ fontWeight: '400', fontSize: '0.8rem' }}
                  className="text-[#2B3674] dark:text-gray-300"
                  text={replyOnMsg?.message}
                />
              ) : replyOnMsg?.type == 'AUDIO' ? (
                <span>
                  <Avatar size={30} shape="square" icon={<GiSoundWaves />} />
                  <Text.Basic
                    style={{ fontWeight: '400', fontSize: '0.8rem' }}
                    className="text-[#2B3674] ml-2"
                    text={'Audio'}
                  />
                </span>
              ) : (
                <span>
                  <Avatar size={20} shape="square" icon={<UserOutlined />} />
                  <Text.Basic
                    style={{ fontWeight: '400', fontSize: '0.8rem' }}
                    className="text-[#2B3674] ml-2"
                    text={'MEDIA'}
                  />
                </span>
              )}
            </div>
          </Col>
          <Col span={1} className="reply-cancel">
            <MdCancel
              size={20}
              style={{ color: Colors.Black }}
              className="cursor-pointer"
              onClick={onClearReply}
            />
          </Col>
        </Row>
      )}

      <div className="composer-row">
        <ComposerEmojiPicker
          onSelect={(emoji) => setMessageInput((previous) => `${previous ?? ''}${emoji}`)}
          disabled={attachmentsBusy || Boolean(audioUrl) || isRecording}
        />
        <Input
          ref={inputRef}
          size="medium"
          onChange={handleChange}
          onPressEnter={
            canSend && !uploadMediaLoader && !isRecording ? () => sendMessage() : undefined
          }
          value={attachmentsBusy || audioUrl || isRecording ? '' : messageInput}
          placeholder={
            attachmentsBusy
              ? 'Press the send button to send images'
              : audioUrl
                ? 'Press the send button to send audio'
                : isRecording
                  ? 'Recording...'
                  : 'Type Message'
          }
          prefix={isRecording ? formatTime(elapsedTime) : ''}
          suffix={suffix}
          style={{
            background: 'var(--panel-2)',
            border: '1.5px solid var(--border)',
            borderRadius: 'var(--composer-r)',
          }}
          className={`background-none`}
          disabled={attachmentsBusy || Boolean(audioUrl)}
        />
        {sendButton}
      </div>

      <input
        id="fileInput"
        type="file"
        style={{ display: 'none' }}
        accept="image/*"
        multiple
        onChange={handleFileInputChange}
      />

      <ComposerAttachments
        images={selectedImages}
        onRemoveImage={(index) =>
          setSelectedImages((previous) => previous.filter((_, i) => i !== index))
        }
        onClearImages={() => setSelectedImages([])}
        audio={audioUrl}
        onRemoveAudio={() => setAudioUrl(null)}
        recordedSeconds={elapsedTime}
        busy={uploadMediaLoader}
      />
    </>
  );
});

export default memo(DirectComposer);
